import { Core } from "@faire/design-tokens";
import { ILeanProductTile } from "@faire/web-api/indigofair/data/ILeanProductTile";
import { useStrictLocalization } from "@faire/web/common/localization";
import { Column, HorizontalLine } from "@faire/web/slate/Layout";
import { getSpacing, Spacer } from "@faire/web/slate/spacing";
import { TypographyStyles } from "@faire/web/slate/Typography";
import { observer } from "mobx-react";
import React from "react";
import styled from "styled-components";

import {
  createFallbackHandler,
  FallbackOpacityAnimation,
} from "@faire/retailer/components/GhostLoading/FallbackHandler";
import { TypographyGhostWithOpacityStyle } from "@faire/retailer/components/GhostLoading/LoadableTypography";
import { TrackedLink } from "@faire/retailer/components/TrackedLink";

import { ProductLinkProps } from "../../ProductTile";

import { LoggedOutBrandDetails } from "./__internal__/LoggedOutBrandDetails/LoggedOutBrandDetails";
import { LoggedOutAdditionalProductDetails } from "./__internal__/LoggedOutProductDetails/LoggedOutAdditionalProductDetails";

export interface ILoggedOutDetailSectionProps {
  productTile: ILeanProductTile | undefined;
  productTileId: string;
  brand: ILeanProductTile.IBrand | undefined;
  generateProductLinkProps?: (
    productToken: string
  ) => ProductLinkProps | undefined;
  generateBrandUrl?: (brandToken: string) => string | undefined;
  onProductLinkClick: (
    productTile: ILeanProductTile,
    event: React.MouseEvent<HTMLAnchorElement>
  ) => void;
  onBrandLinkClick: (event: React.MouseEvent<HTMLAnchorElement>) => void;
}

const LoggedOutDetailSectionBase = ({
  productTile,
  productTileId,
  brand,
  generateProductLinkProps,
  generateBrandUrl,
  onProductLinkClick,
  onBrandLinkClick,
}: ILoggedOutDetailSectionProps) => {
  const { strictLocalize } = useStrictLocalization();

  const product = productTile?.product;
  const productToken = product?.token;
  const productName =
    product?.name ??
    strictLocalize({
      defaultMessage: "Product name",
      description: {
        text: "Used as a generic placeholder for the product name for Discovery tiles whilst loading.",
      },
    });
  const isProductLoaded = !!product;

  const onProductClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
    if (productTile) {
      onProductLinkClick?.(productTile, event);
    }
  };

  const productLinkProps =
    productToken && generateProductLinkProps
      ? generateProductLinkProps(productToken)
      : undefined;

  return (
    <DetailSectionColumn>
      <HorizontalLine />
      <ProductDetailsColumn>
        <TrackedLink
          onClick={onProductClick}
          to={productLinkProps?.productUrl}
          token={productToken}
          data-test-id="product-tile-link"
          style={{ width: "100%" }}
          replace={productLinkProps?.replace}
        >
          <Column style={{ padding: getSpacing("2x") }}>
            <ProductNameFallback
              htmlFor={productTileId}
              isLoaded={isProductLoaded}
            >
              {productName}
            </ProductNameFallback>
            <Spacer height="1x" />
            <LoggedOutAdditionalProductDetails productTile={productTile} />
          </Column>
        </TrackedLink>
      </ProductDetailsColumn>
      {brand ? (
        <>
          <HorizontalLine />
          <Column style={{ width: "100%" }}>
            <LoggedOutBrandDetails
              brand={brand}
              brandRatingValue={product?.avg_brand_review_rating}
              brandRatingCount={product?.brand_review_count}
              productToken={productToken}
              onBrandLinkClick={onBrandLinkClick}
              generateBrandUrl={generateBrandUrl}
              isProductLoaded={isProductLoaded}
            />
          </Column>
        </>
      ) : null}
    </DetailSectionColumn>
  );
};

export const LoggedOutDetailSection = observer(LoggedOutDetailSectionBase);

const DetailSectionColumn = styled(Column)`
  width: 100%;
  height: 100%;
  background-color: ${Core.surface.inverse};
`;

const ProductDetailsColumn = styled(Column)`
  width: 100%;
  flex-grow: 1;
`;

const ProductName = styled.label`
  ${TypographyStyles({ variant: "paragraphSansMedium" })};
  align-self: stretch;
  min-height: 40px;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  cursor: pointer;
`;

const ProductNameFallback = createFallbackHandler(ProductName, {
  backgroundStyle: TypographyGhostWithOpacityStyle("0.2s"),
  loadedStyle: FallbackOpacityAnimation("0.2s"),
});
